/**
 * Business constants from AGROFARM_DEVELOPMENT_PLAN.md §17.6 and the BR-x rules.
 *
 * These live here, shared by server and client, so a rule can never mean one
 * thing in the form and another in the service that validates it.
 *
 * Values marked "seeded default" are written into the settings row at install
 * time and read from the database thereafter — the partners can change them
 * without a deploy. The constants below are only the initial values.
 */

import { SPECIES, type Species } from './enums.js';

/** §17.6 — gestation periods driving BR-17 expected delivery dates. */
export const GESTATION_DAYS = {
  [SPECIES.CATTLE]: 283,
  [SPECIES.GOAT]: 150,
} as const satisfies Record<Species, number>;

/** §17.6 — reference daily weight gain used for the growth band (P2-TR4). */
export const EXPECTED_DAILY_GAIN_GRAMS = {
  [SPECIES.CATTLE]: 700,
  [SPECIES.GOAT]: 100,
} as const satisfies Record<Species, number>;

/**
 * The heaviest an animal of each species could plausibly be, in kilograms.
 *
 * A sanity ceiling on a typed weight, not a biological claim. Its job is to
 * catch the two mistakes a scale and a thumb actually produce: a decimal point
 * in the wrong place (2250 for 22.50) and a stray extra digit. A goat is not
 * 200 kg and a bull on this farm is not two tonnes, so either is a typo that
 * would otherwise flatten the growth curve and mislead the qurbani target.
 */
export const MAX_PLAUSIBLE_WEIGHT_KG = {
  [SPECIES.CATTLE]: 1200,
  [SPECIES.GOAT]: 150,
} as const satisfies Record<Species, number>;

/**
 * BR-14 — feed cost allocation weight. One head of cattle eats roughly as much
 * as five goats, so per-animal feed cost is allocated in "goat-units".
 * Seeded default for `settings.cattle_feed_unit`.
 */
export const DEFAULT_CATTLE_FEED_UNIT = 5.0;

/** BR-06 — expenses at or above this amount require a receipt photo. Seeded default. */
export const DEFAULT_RECEIPT_REQUIRED_ABOVE = '500.00';

/** BR-09 — a purchase above this, with a useful life over a year, is an asset not an expense. */
export const ASSET_CAPITALIZATION_THRESHOLD = '5000.00';

/** BR-11 — Bangladesh financial year runs July to June. Seeded default. */
export const DEFAULT_FY_START_MONTH = 7;

/** §1 — the partners' initial capital, the target of the break-even tracker. */
export const INITIAL_CAPITAL_TARGET = '600000.00';

/** BR-21 / P2-BR7 — stock variance above this fraction is flagged on the dashboard. */
export const STOCK_VARIANCE_THRESHOLD = 0.05;

/** BR-27 — a category is flagged red once actual spend passes this share of budget. */
export const BUDGET_ALERT_THRESHOLD = 0.9;

/** BR-22 — medicines are flagged this many days before expiry. */
export const MEDICINE_EXPIRY_WARNING_DAYS = 30;

/** P2-BR6 — reminder lead times, in days. */
export const REMINDER_LEAD_DAYS = {
  VACCINE: 7,
  DELIVERY_EARLY: 30,
  DELIVERY_FINAL: 7,
  LOAN_INSTALLMENT: 7,
} as const;

/** P2-BR8 — an active animal should be weighed at least this often. */
export const WEIGHT_LOG_INTERVAL_DAYS = 30;

/** §17.6 / P3-BR4 — each account should be reconciled this often; overdue past 35 days. */
export const RECONCILIATION_INTERVAL_DAYS = 30;
export const RECONCILIATION_OVERDUE_DAYS = 35;

/** P1-BR8 — an entry dated more than this many days before its creation is flagged as backdated. */
export const BACKDATE_FLAG_THRESHOLD_DAYS = 3;

/** SEC-2 — brute-force guard (§8.4). */
export const LOGIN_MAX_ATTEMPTS = 5;
export const LOGIN_LOCKOUT_MINUTES = 15;

/** P0-BR3 — short-lived access token, long-lived refresh session. */
export const ACCESS_TOKEN_TTL_MINUTES = 15;
export const REFRESH_TOKEN_TTL_DAYS = 30;

/** §7 / SEC-5 — upload limits. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'] as const;
/** P1-TR4 — client-side compression target before upload, for weak rural networks. */
export const CLIENT_IMAGE_COMPRESSION_TARGET_BYTES = 300 * 1024;

/** AP-3 — pagination. The maximum is a resource-exhaustion guard (SEC-4). */
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

/** BR-24 — receivables aging buckets, in days. */
export const AGING_BUCKETS = [
  { label: '0-30', minDays: 0, maxDays: 30 },
  { label: '31-60', minDays: 31, maxDays: 60 },
  { label: '60+', minDays: 61, maxDays: null },
] as const;

/** AP-7 / NFR-9 — store UTC, display Dhaka. */
export const DISPLAY_TIMEZONE = 'Asia/Dhaka';

/** NFR-6 — Bangla is the default; the toggle is per user (P0-BR4). */
export const SUPPORTED_LANGUAGES = ['bn', 'en'] as const;
export const DEFAULT_LANGUAGE = 'bn';

/** BR-12 — tag prefixes. Uniqueness is enforced by the database, not by this constant. */
export const TAG_PREFIX = {
  [SPECIES.GOAT]: 'G',
  [SPECIES.CATTLE]: 'C',
} as const satisfies Record<Species, string>;

/** BR-12 — G-001 / C-001. Three digits covers a farm many times this one's size. */
export const TAG_NUMBER_PADDING = 3;
export const TAG_PATTERN = /^[GC]-\d{3,}$/;
